import { differenceInCalendarDays } from 'date-fns'
import type { HealthStatus, Monitor } from '../types/monitor'

export function daysUntil(iso: string): number {
  return differenceInCalendarDays(new Date(iso), new Date())
}

export function certificateDaysRemaining(monitor: Monitor): number | null {
  if (!monitor.certificate) {
    return null
  }
  return daysUntil(monitor.certificate.validTo)
}

/** Certificate health for one monitor, judged against its own warning window. */
export function sslStatus(monitor: Monitor): HealthStatus {
  if (!monitor.sslEnabled) {
    return 'paused'
  }
  if (!monitor.certificate) {
    return 'critical'
  }
  const days = daysUntil(monitor.certificate.validTo)
  if (days < 0 || !monitor.certificate.chainValid) {
    return 'critical'
  }
  if (days <= monitor.warningWindowDays) {
    return 'warning'
  }
  return 'healthy'
}

export function uptimeStatus(monitor: Monitor): HealthStatus {
  if (!monitor.uptimeEnabled) {
    return 'paused'
  }
  if (!monitor.isUp) {
    return 'critical'
  }
  if (monitor.availability24h < 99.5) {
    return 'warning'
  }
  return 'healthy'
}

const severity: Record<HealthStatus, number> = {
  critical: 3,
  warning: 2,
  healthy: 1,
  paused: 0,
}

export function overallStatus(monitor: Monitor): HealthStatus {
  const ssl = sslStatus(monitor)
  const uptime = uptimeStatus(monitor)
  return severity[ssl] >= severity[uptime] ? ssl : uptime
}

export function statusRank(status: HealthStatus): number {
  return severity[status]
}

export function servicesUsed(monitors: Monitor[]): number {
  return monitors.reduce(
    (total, monitor) =>
      total + (monitor.sslEnabled ? 1 : 0) + (monitor.uptimeEnabled ? 1 : 0),
    0,
  )
}

export const statusLabels: Record<HealthStatus, string> = {
  healthy: 'Healthy',
  warning: 'Warning',
  critical: 'Critical',
  paused: 'Not monitored',
}

/**
 * The single thing most worth the customer's attention right now, if anything is.
 * A site that is currently down always beats a certificate that is merely close.
 */
export function findTopConcern(monitors: Monitor[]): {
  monitor: Monitor
  kind: 'down' | 'expiring' | 'expired' | 'unreadable'
  days: number | null
} | null {
  const down = monitors.filter((m) => uptimeStatus(m) === 'critical')
  if (down.length > 0) {
    return { monitor: down[0], kind: 'down', days: null }
  }

  const unreadable = monitors.filter(
    (m) => m.sslEnabled && !m.certificate,
  )
  if (unreadable.length > 0) {
    return { monitor: unreadable[0], kind: 'unreadable', days: null }
  }

  const withCerts = monitors
    .filter((m) => m.sslEnabled && m.certificate)
    .map((m) => ({ monitor: m, days: daysUntil(m.certificate!.validTo) }))
    .sort((a, b) => a.days - b.days)

  const soonest = withCerts[0]
  if (!soonest) {
    return null
  }
  if (soonest.days < 0) {
    return { monitor: soonest.monitor, kind: 'expired', days: soonest.days }
  }
  if (soonest.days <= soonest.monitor.warningWindowDays) {
    return { monitor: soonest.monitor, kind: 'expiring', days: soonest.days }
  }
  return null
}
